<template>
  <div>
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-lg font-semibold text-gray-800">Hospice</h2>
    </div>

    <!-- Methodology -->
    <div v-if="windowStart && windowEnd" class="text-xs text-gray-500 mb-3" style="line-height: 1.6;">
      Claims from <span class="font-medium text-gray-700">{{ formatDateStr(windowStart) }}</span>
      through <span class="font-medium text-gray-700">{{ formatDateStr(windowEnd) }}</span>.
      Attribution panel as of <span class="font-medium text-gray-700">{{ formatDateStr(selectedReportDt) }}</span>.
      IP cost is {{ timeWindow === 'ytd' ? 'year-to-date' : 'rolling 12-month' }} inpatient spend for the same patients.
    </div>

    <!-- Duration distribution -->
    <div v-if="durationStats" class="bg-white border border-gray-200 rounded-lg p-5 mb-4">
      <div class="flex items-center gap-8">
        <div class="flex-1">
          <div class="text-xs text-gray-500 mb-2">Duration Distribution</div>
          <div class="flex h-8 rounded overflow-hidden mb-2">
            <div
              v-for="bucket in durationStats.buckets"
              :key="bucket.label"
              :style="{ width: bucket.pct + '%', backgroundColor: bucket.color }"
              class="flex items-center justify-center transition-all relative group"
              :title="bucket.label + ': ' + bucket.count + ' patients (' + bucket.pct + '%)'"
            >
              <span v-if="bucket.pct >= 8" class="text-[10px] font-medium text-white drop-shadow-sm">{{ bucket.pct }}%</span>
            </div>
          </div>
          <div class="flex items-center gap-5">
            <div v-for="bucket in durationStats.buckets" :key="'l-' + bucket.label" class="flex items-center gap-1.5">
              <div class="w-2.5 h-2.5 rounded-sm" :style="{ backgroundColor: bucket.color }" />
              <span class="text-[11px] text-gray-500">{{ bucket.label }}</span>
              <span class="text-[11px] font-medium text-gray-700">{{ bucket.count }}</span>
            </div>
          </div>
        </div>
        <div class="flex-shrink-0 grid grid-cols-2 gap-x-6 gap-y-3">
          <div>
            <div class="text-xs text-gray-500 mb-1">Patients</div>
            <div class="text-lg font-semibold text-gray-800">{{ durationStats.total.toLocaleString() }}</div>
          </div>
          <div>
            <div class="text-xs text-gray-500 mb-1">Deceased</div>
            <div class="text-lg font-semibold text-gray-500">{{ durationStats.deceased.toLocaleString() }}</div>
          </div>
          <div>
            <div class="text-xs text-gray-500 mb-1">Total Hospice Cost</div>
            <div class="text-lg font-semibold text-gray-800">{{ formatCurrencyVal(durationStats.totalCost) }}</div>
          </div>
          <div>
            <div class="text-xs text-gray-500 mb-1">Avg Duration</div>
            <div class="text-lg font-semibold text-gray-800">{{ durationStats.avgMonths }} mo</div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="py-10 text-center text-sm text-gray-400">Loading…</div>
    <DataTable
      v-else
      :columns="columns"
      :rows="rowData"
      row-key="mbi"
      :row-class="rowClass"
      max-height="600px"
      saved-view-key="cclf-hospice"
      :has-saved="hasSavedView"
      :saved-just-saved="savedViewJustSaved"
      v-model:sort-model="sortModel"
      export-title="Hospice"
      export-filename="cclf_hospice"
      empty-text="No hospice data."
      @save-view="saveView"
      @reset-view="resetView"
    >
      <template #toolbar-left>
        <select v-model="selectedReportDt" class="hos-ctrl" @change="loadData">
          <option v-for="d in reportDates" :key="d" :value="d">{{ d }}</option>
        </select>
        <select v-model="timeWindow" class="hos-ctrl" @change="loadData">
          <option value="r12">Rolling 12mo</option>
          <option value="ytd">YTD</option>
        </select>
        <select v-model="careSetting" class="hos-ctrl" @change="loadData">
          <option value="">All Settings</option>
          <option value="SNF">SNF</option>
          <option value="ALF">ALF</option>
        </select>
      </template>
    </DataTable>
  </div>
</template>

<script>
import { DataTable } from '@/components/External/table'
import savedView from '@/mixins/savedView'

export default {
  name: 'CCLFReport_Hospice',
  components: { DataTable },
  mixins: [savedView],
  savedView: {
    key: 'cclf-hospice',
    fields: ['timeWindow', 'careSetting', 'sortModel'],
    scopeProp: 'entityId',
  },
  props: {
    contractId: { type: String, default: null },
    performanceYear: { type: Number, default: null },
    dosthru: { type: String, default: null },
    entityId: { type: Number, default: null },
  },

  data() {
    return {
      careSetting: '',
      timeWindow: 'r12',
      selectedReportDt: null,
      reportDates: [],
      windowStart: null,
      windowEnd: null,
      rowData: [],
      loading: false,
      sortModel: { key: null, dir: 'asc' },
      columns: [
        { field: 'patient_name', header: 'Patient', type: 'text', sticky: true, width: 160, filterable: true, cellClass: () => 'sensitive' },
        { field: 'mbi', header: 'MBI', type: 'text', width: 120, filterable: true, cellClass: () => 'sensitive' },
        { field: 'patient_status', header: 'Status', type: 'text', width: 90, filterable: true },
        { field: 'attributed_facility', header: 'Attributed Facility', type: 'text', width: 200, filterable: true, cellClass: () => 'sensitive' },
        { field: 'hospice_provider', header: 'Hospice Company', type: 'text', width: 200, filterable: true, cellClass: () => 'sensitive' },
        { field: 'hospice_start', header: 'Start', type: 'date', width: 100 },
        { field: 'hospice_last', header: 'Last Claim', type: 'date', width: 110 },
        { field: 'last_cert_date', header: 'Last Cert', type: 'date', width: 100 },
        { field: 'months_on_hospice', header: 'Months', type: 'number', width: 80, cellStyle: v => this.durationStyle(v) },
        { field: 'los_9_months_plus', header: '9+ Mo', type: 'text', width: 70,
          cellStyle: v => v === 'Y' ? { color: '#dc2626', fontWeight: 'bold' } : null },
        { field: 'hospice_cost', header: 'Hospice Cost', type: 'currency', width: 120 },
        { field: 'ip_cost', header: 'IP Cost', type: 'currency', width: 120 },
        { field: 'hospice_claims', header: 'Claims', type: 'number', width: 80 },
        { field: 'ip_claims', header: 'IP Claims', type: 'number', width: 90 },
        { field: 'date_of_death', header: 'Death', type: 'date', width: 100, cellClass: () => 'sensitive' },
      ],
    }
  },

  computed: {
    durationStats() {
      if (!this.rowData.length) return null
      const total = this.rowData.length
      const deceased = this.rowData.filter(r => r.date_of_death || r.patient_status === 'Deceased').length
      const totalCost = this.rowData.reduce((s, r) => s + (r.hospice_cost || 0), 0)
      const totalMonths = this.rowData.reduce((s, r) => s + (r.months_on_hospice || 0), 0)
      const lt3 = this.rowData.filter(r => (r.months_on_hospice || 0) < 3).length
      const lt6 = this.rowData.filter(r => (r.months_on_hospice || 0) >= 3 && (r.months_on_hospice || 0) < 6).length
      const lt12 = this.rowData.filter(r => (r.months_on_hospice || 0) >= 6 && (r.months_on_hospice || 0) < 12).length
      const gte12 = this.rowData.filter(r => (r.months_on_hospice || 0) >= 12).length
      return {
        total,
        deceased,
        totalCost,
        avgMonths: total ? (totalMonths / total).toFixed(1) : '0',
        buckets: [
          { label: '< 3 mo', count: lt3, pct: Math.round((lt3 / total) * 100), color: 'rgba(34, 197, 94, 0.7)' },
          { label: '3-6 mo', count: lt6, pct: Math.round((lt6 / total) * 100), color: 'rgba(234, 179, 8, 0.7)' },
          { label: '6-12 mo', count: lt12, pct: Math.round((lt12 / total) * 100), color: 'rgba(249, 115, 22, 0.7)' },
          { label: '12+ mo', count: gte12, pct: Math.round((gte12 / total) * 100), color: 'rgba(239, 68, 68, 0.7)' },
        ],
      }
    },
  },

  watch: {
    contractId() { this.loadData() },
    performanceYear() { this.loadData() },
    dosthru() { this.loadData() },
    entityId() { this.loadData() },
  },

  mounted() {
    if (this.contractId && this.performanceYear) this.loadData()
  },

  methods: {
    // Called by the mixin's resetView() after it clears storage.
    onResetView() {
      this.timeWindow = 'r12'
      this.careSetting = ''
      this.loadData()
    },
    rowClass(row) {
      return (row.date_of_death || row.patient_status === 'Deceased') ? 'hos-deceased-row' : null
    },
    durationStyle(val) {
      if (val == null) return null
      if (val < 3) return { backgroundColor: 'rgba(34, 197, 94, 0.12)' }
      if (val < 6) return { backgroundColor: 'rgba(234, 179, 8, 0.15)' }
      if (val < 12) return { backgroundColor: 'rgba(249, 115, 22, 0.18)' }
      return { backgroundColor: 'rgba(239, 68, 68, 0.2)', fontWeight: 600 }
    },

    aggregateClaims(hospiceClaims, ipClaims) {
      // Group hospice claims by patient
      const patients = {}
      for (const c of hospiceClaims) {
        const mbi = c.bene_mbi_id
        if (!mbi) continue
        if (!patients[mbi]) {
          patients[mbi] = {
            mbi,
            bene_first_name: c.bene_first_name,
            bene_last_name: c.bene_last_name,
            patient_name: ((c.bene_first_name || '') + ' ' + (c.bene_last_name || '')).trim() || mbi,
            patient_status: c.patient_status || null,
            attributed_facility: c.attributed_facility || null,
            date_of_death: c.bene_death_dt || null,
            hospice_cost: 0,
            hospice_claims: 0,
            hospice_start: null,
            hospice_last: null,
            last_cert_date: null,
            bene_ptnt_stus_cd: null,
            providers: {},  // npi → { name, cost }
          }
        }
        const p = patients[mbi]
        const amt = Number(c.clm_pmt_amt) || 0
        p.hospice_cost += amt
        p.hospice_claims += 1
        if (c.clm_from_dt && (!p.hospice_start || c.clm_from_dt < p.hospice_start)) p.hospice_start = c.clm_from_dt
        if (c.clm_thru_dt && (!p.hospice_last || c.clm_thru_dt > p.hospice_last)) p.hospice_last = c.clm_thru_dt
        if (c.clm_from_dt && (!p.last_cert_date || c.clm_from_dt > p.last_cert_date)) p.last_cert_date = c.clm_from_dt
        if (c.bene_ptnt_stus_cd) p.bene_ptnt_stus_cd = c.bene_ptnt_stus_cd
        // Track provider cost
        const npi = c.fac_prvdr_npi_num || 'Unknown'
        if (!p.providers[npi]) p.providers[npi] = { name: c.provider_org_name || npi, cost: 0 }
        p.providers[npi].cost += amt
      }

      // Build IP cost map
      const ipMap = {}
      const ipClaimCount = {}
      for (const c of ipClaims) {
        const mbi = c.bene_mbi_id
        if (!mbi) continue
        ipMap[mbi] = (ipMap[mbi] || 0) + (Number(c.clm_pmt_amt) || 0)
        ipClaimCount[mbi] = (ipClaimCount[mbi] || 0) + 1
      }

      // Build output rows
      return Object.values(patients).map(p => {
        // Primary provider = highest cost
        let provName = 'Unknown'
        let maxCost = -1
        for (const [, info] of Object.entries(p.providers)) {
          if (info.cost > maxCost) { maxCost = info.cost; provName = info.name }
        }
        // Duration in months
        let months = null
        if (p.hospice_start && p.hospice_last) {
          const d1 = new Date(p.hospice_start)
          const d2 = new Date(p.hospice_last)
          months = Math.max(1, (d2.getFullYear() - d1.getFullYear()) * 12 + d2.getMonth() - d1.getMonth())
        }
        return {
          mbi: p.mbi,
          patient_name: p.patient_name,
          patient_status: p.patient_status || 'Active',
          attributed_facility: p.attributed_facility || 'Unknown',
          date_of_death: p.date_of_death,
          bene_ptnt_stus_cd: p.bene_ptnt_stus_cd,
          hospice_provider: provName,
          hospice_start: p.hospice_start,
          hospice_last: p.hospice_last,
          last_cert_date: p.last_cert_date,
          months_on_hospice: months,
          los_9_months_plus: months && months >= 9 ? 'Y' : 'N',
          hospice_cost: Math.round(p.hospice_cost * 100) / 100,
          hospice_claims: p.hospice_claims,
          ip_cost: ipMap[p.mbi] || null,
          ip_claims: ipClaimCount[p.mbi] || 0,
        }
      })
    },

    async loadData() {
      if (!this.contractId || !this.performanceYear) return
      this.loading = true
      try {
        const response = await this.$axios.post('/api/portfolio_management/cclf-report/hospice/', {
          contract_id: this.contractId,
          performance_year: this.performanceYear,
          dosthru: this.dosthru,
          entity_id: this.entityId,
          care_setting: this.careSetting || undefined,
          report_dt: this.selectedReportDt || undefined,
          time_window: this.timeWindow,
        })
        const data = response.data
        this.reportDates = data.report_dates || []
        // Sync dropdown to the report_dt the backend actually used
        if (data.report_dt) this.selectedReportDt = data.report_dt
        this.windowStart = data.window_start || null
        this.windowEnd = data.window_end || null
        this.rowData = this.aggregateClaims(data.hospice_claims || [], data.ip_claims || [])
      } catch (error) {
        console.error('Failed to load hospice data:', error)
      } finally {
        this.loading = false
      }
    },

    formatCurrencyVal(val) {
      if (val == null) return '--'
      return '$' + Number(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    },
    formatDateStr(val) {
      if (!val) return ''
      return new Date(val + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
  },
}
</script>

<style scoped>
.hos-ctrl { font-size: 12px; border: 1px solid #E8E3DC; border-radius: 6px; padding: 4px 8px; background: #fff; color: #4A4540; }
:deep(.hos-deceased-row) td { color: #9ca3af; font-style: italic; }
</style>
